import React, { ReactElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTextSelection } from './billTextSelectionHook';
import { BillAPI } from '../../../api';
import { SortOrder, SortBy } from '../../../models/SortOrder';

type BillTextPhraseSearchPeekProps = {
  billTextRef: React.RefObject<HTMLElement>;
};

const SEARCH_PHRASE_PEEK_ANCHOR_ID = 'bill-text-search-phrase-peek-anchor';

const BillTextPhraseSearchPeek = (
  props: BillTextPhraseSearchPeekProps,
): ReactElement => {
  // state
  const billTextRefCurrent =
    props.billTextRef.current === null ? undefined : props.billTextRef.current;
  const { clientRect, isCollapsed, textContent } = useTextSelection(billTextRefCurrent);
  const selectionIsCollapsed = isCollapsed;

  // search
  const params = useMemo(
    () => ({
      criteria: {
        jurisdictions: ['mn'],
        sessionNames: ['2021-2022 Regular Session'],
        query: `"${textContent}"`,
      },
      pagination: {
        page: 1,
        pageSize: 10,
        sortOrder: new SortOrder(SortBy.RELEVANCE, false),
      },
    }),
    [textContent],
  );
  const { data: results, isSuccess } = BillAPI.endpoints.searchBills.useQuery(params);

  const showPeek =
    selectionIsCollapsed === false && !!results && isSuccess && results.data.length > 0;

  return (
    <>
      {/* Invisible anchor tracking the selection; react-tooltip positions the peek
          relative to it and keeps it in view (flipping/shifting) automatically. */}
      <span
        id={SEARCH_PHRASE_PEEK_ANCHOR_ID}
        style={{
          position: 'absolute',
          top: clientRect ? clientRect.top : 0,
          left: clientRect ? clientRect.left : 0,
          width: 1,
          height: 1,
          pointerEvents: 'none',
        }}
      />
      <ReactTooltip
        anchorSelect={`#${SEARCH_PHRASE_PEEK_ANCHOR_ID}`}
        className="search-phrase-peek"
        clickable
        isOpen={showPeek}
        place="bottom"
        positionStrategy="fixed"
        setIsOpen={() => {}}
        style={{
          color: '#f68c24',
          width: '400px',
          border: '1px solid #000',
          backgroundColor: '#fff',
          transition: 'opacity 0.1s ease-out',
        }}
      >
        <ul>
          {results?.data.map((bill) => (
            <li key={bill.id}>
              <Link
                rel="noopener noreferrer"
                target="_blank"
                to={`/legislative-tracking/bill/details/${bill.id}`}
              >
                {bill.number} {bill.name}
              </Link>
            </li>
          ))}
        </ul>
      </ReactTooltip>
    </>
  );
};

export default BillTextPhraseSearchPeek;
